import Phaser from 'phaser';
import { Cell } from '../objects/Cell';
import { NutrientSystem } from '../systems/NutrientSystem';
import { EnvironmentSystem } from '../systems/EnvironmentSystem';
import { FpsMonitor } from '../utils/FpsMonitor';
import { GameSettings } from '../utils/GameSettings';
import { GameUISystem, UIComponent } from '../systems/GameUISystem';
import { SharedResources } from '../utils/SharedResources';
import { Nucleus } from '../objects/Nucleus';

export class GameScene extends Phaser.Scene {
  private cell!: Cell;
  private nutrientSystem!: NutrientSystem;
  private environmentSystem!: EnvironmentSystem;
  private backButton!: Phaser.GameObjects.Text;
  private fullscreenButton!: Phaser.GameObjects.Image;
  private gradientBackground!: Phaser.GameObjects.Sprite;
  
  // Camera and world settings
  private cameraControls: {
    isDragging: boolean;
    lastPointerPosition: { x: number, y: number };
    minZoom: number;
    maxZoom: number;
    zoomFactor: number;
    targetZoom: number;
    currentZoom: number;
    zoomEase: number;
    zoomPoint: Phaser.Math.Vector2;
    panEase: number;
    targetPan: Phaser.Math.Vector2;
    currentPan: Phaser.Math.Vector2;
  };
  
  // UI elements
  private uiContainer!: Phaser.GameObjects.Container;
  private energyText!: Phaser.GameObjects.Text;
  private healthText!: Phaser.GameObjects.Text;
  private gameUI!: GameUISystem;
  
  // Add properties for evolution data
  private membraneStability: number = 100; // Fully stable in this stage
  private cytoskeletonDensity: number = 100; // Fully developed in this stage
  private atpEfficiency: number = 100; // Fully efficient in this stage
  private hasLysosomes: boolean = false; // Unlocked in this stage
  private hasChloroplasts: boolean = false; // Can be unlocked in this stage
  
  // Add FpsMonitor property
  private fpsMonitor!: FpsMonitor;
  private settingsManager!: GameSettings;
  
  // Add lastFpsCheck property
  private lastFpsCheck: number | undefined;
  
  constructor() {
    super('GameScene');
    
    // Initialize camera controls with better balance between smoothness and responsiveness
    this.cameraControls = {
      isDragging: false,
      lastPointerPosition: { x: 0, y: 0 },
      minZoom: 0.1,
      maxZoom: 5,
      zoomFactor: 0.1, // Increased for more responsive zooming
      targetZoom: 1,
      currentZoom: 1,
      zoomEase: 0.07, // Increased for more responsive zooming while still smooth
      zoomPoint: new Phaser.Math.Vector2(0, 0),
      panEase: 0.1, // Increased for more responsive panning
      targetPan: new Phaser.Math.Vector2(0, 0),
      currentPan: new Phaser.Math.Vector2(0, 0)
    };
  }

  create(): void {
    // Initialize settings manager
    this.settingsManager = GameSettings.getInstance();
    
    // Initialize shared resources
    const sharedResources = SharedResources.getInstance();
    if (!sharedResources.hasGradientTexture()) {
      // Initialize with this scene if not already initialized
      sharedResources.initialize(this);
    }
    
    // No world bounds for true infinite scrolling
    // The physics world will still exist but without boundaries
    
    // Set a solid dark background color for the entire world
    this.cameras.main.setBackgroundColor('#000000');
    
    // Initialize systems
    this.nutrientSystem = new NutrientSystem(this);
    this.environmentSystem = new EnvironmentSystem(this);
    
    // Create main cell in the center
    this.cell = new Cell(
      this, 
      0, // Center X (world origin)
      0  // Center Y (world origin)
    );
    
    // Add nucleus to cell in single-player mode with high detail level
    this.addNucleusToCell();
    
    // Configure main camera
    this.cameras.main.setName('mainCam');
    // Don't set bounds on the camera to allow infinite panning
    this.cameras.main.setZoom(1);
    
    // Create a separate UI camera that doesn't move or zoom
    const uiCam = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    uiCam.setName('uiCam');
    uiCam.setScroll(0, 0);
    uiCam.transparent = true;
    
    // Create UI
    this.createUI();
    
    // Create gradient background using shader approach
    this.createBackground();
    console.log('Created background for game scene');
    
    // Set camera masks to ensure proper rendering - gradientBackground should be visible to main camera
    this.cameras.main.ignore(this.uiContainer);
    uiCam.ignore([this.cell, this.gradientBackground]); // UI camera should ignore gradient
    
    // Setup input handlers
    this.setupInput();
    
    // Setup camera controls
    this.setupCameraControls();
    
    // Handle window resize
    this.scale.on('resize', this.handleResize, this);
    
    // Add custom window resize event handler for better control
    this.events.on('windowResized', this.handleWindowResized, this);
    
    // Initial resize handling
    this.handleResize(this.scale.gameSize);
    
    // Don't use Phaser's built-in follow - we'll implement our own smoother follow
    // that doesn't react to small cell shape changes
    this.cameras.main.stopFollow();
    
    // Initialize camera control vectors
    this.cameraControls.targetPan.set(this.cell.x, this.cell.y);
    this.cameraControls.currentPan.set(this.cell.x, this.cell.y);
    
    // Set initial camera position
    this.cameras.main.centerOn(this.cell.x, this.cell.y);
    
    // Debug: Count all sprites to verify we only have one gradient
    console.log('--- Counting gradientTexture sprites ---');
    let gradientCount = 0;
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Sprite && child.texture.key === 'gradientTexture') {
        gradientCount++;
        console.log(`Gradient sprite #${gradientCount}:`, 
                    'name:', child.name || 'unnamed', 
                    'position:', child.x, child.y,
                    'depth:', child.depth);
      }
    });
    console.log(`Total gradient sprites: ${gradientCount}`);
    
    // Debug: Log all graphics objects in the scene
    this.debugLogAllGraphics();
  }

  update(time: number, delta: number): void {
    // Update the camera controls
    this.updateCamera(delta);
    
    // Update systems
    this.nutrientSystem.update(delta);
    this.environmentSystem.update(delta);
    
    // Update cell
    this.cell.update(delta);
    
    // Update UI text with current values
    if (this.cell) {
      const energy = this.cell.getEnergy();
      const health = this.cell.getHealth();
      
      // Use the new updateUI method
      this.gameUI.updateUI({
        'energy': {
          value: energy,
          text: `Energy: ${energy}%`
        },
        'health': {
          value: health,
          text: `Health: ${health}%`
        }
      }, delta);
    }
    
    // Scan for any movement markers or particles that need to be ignored by the UI camera
    this.ensureAllEffectsIgnoredByUICamera();
    
    // Every 2 seconds, check if FPS monitor visibility matches settings
    if (this.lastFpsCheck === undefined) {
      this.lastFpsCheck = 0;
    }
    
    if (time > this.lastFpsCheck + 2000) {
      this.lastFpsCheck = time;
      
      if (this.settingsManager && this.fpsMonitor) {
        const settings = this.settingsManager.getSettings();
        if (settings.showFps !== this.fpsMonitor.isVisible()) {
          console.log(`Fixing FPS display visibility in GameScene: settings=${settings.showFps}, current=${this.fpsMonitor.isVisible()}`);
          this.fpsMonitor.toggleDisplay(settings.showFps);
        }
      }
    }
  }

  private updateCamera(delta: number): void {
    // Normalize delta to ensure consistent camera movement regardless of frame rate
    const normalizedDelta = delta / 16.67; // 60 FPS = 16.67ms per frame
    
    // Apply smooth zooming
    if (Math.abs(this.cameraControls.currentZoom - this.cameraControls.targetZoom) > 0.001) {
      // Calculate the difference and apply easing with frame rate compensation
      const zoomDiff = this.cameraControls.targetZoom - this.cameraControls.currentZoom;
      const adjustedEase = Math.min(this.cameraControls.zoomEase * normalizedDelta, 1);
      
      // Make zooming more responsive when zooming out
      const zoomDirection = Math.sign(zoomDiff);
      const zoomEaseFactor = zoomDirection < 0 ? 1.5 : 1.0; // Faster when zooming out
      
      this.cameraControls.currentZoom += zoomDiff * adjustedEase * zoomEaseFactor;
      
      // Apply the new zoom level without rounding to avoid jitter
      this.cameras.main.setZoom(this.cameraControls.currentZoom);
      
      // If we're very close to the target zoom, snap to it
      if (Math.abs(this.cameraControls.currentZoom - this.cameraControls.targetZoom) < 0.005) {
        this.cameraControls.currentZoom = this.cameraControls.targetZoom;
        this.cameras.main.setZoom(this.cameraControls.currentZoom);
      }
    }
    
    // Only update camera position if not dragging
    if (!this.cameraControls.isDragging) {
      // Use Phaser's built-in follow for the cell, but with a fixed offset
      // This is much more stable than our custom follow system
      this.cameras.main.centerOn(this.cell.x, this.cell.y);
    }
  }

  private createUI(): void {
    // Initialize the GameUISystem with our UI container
    this.uiContainer = this.add.container(0, 0);
    this.gameUI = new GameUISystem(this, {
      containerDepth: 1000,
      buttonBgColor: 0x1a6dd7,
      // Set specific colors for energy and health bars
      progressBarColor: 0x00aaff  // Default blue color for progress bars
    });
    
    // Add energy progress bar
    this.gameUI.createProgressBar(
      'energy',
      20,
      20,
      'Energy',
      100  // Initial value is 100%
    );
    
    // Add health progress bar with custom color
    const healthProgressBar = this.gameUI.createProgressBar(
      'health',
      20,
      80,
      'Health',
      100  // Initial value is 100%
    );
    
    // Change the color of the health bar to green
    const healthProgressData = this.gameUI.getProgressBar('health');
    if (healthProgressData && healthProgressData.fill) {
      healthProgressData.fill.setFillStyle(0x4CAF50);  // Green color
    }
    
    // Register health bar color updater component
    this.gameUI.registerComponent({
      id: 'healthBarColorUpdater',
      update: (data: any) => {
        if (typeof data !== 'object' || !('value' in data)) return;
        
        const health = data.value as number;
        const healthProgressData = this.gameUI.getProgressBar('health');
        
        if (healthProgressData && healthProgressData.fill) {
          if (health < 25) {
            healthProgressData.fill.setFillStyle(0xFF5252);  // Red when low health
          } else if (health < 50) {
            healthProgressData.fill.setFillStyle(0xFFB74D);  // Orange when medium health
          } else {
            healthProgressData.fill.setFillStyle(0x4CAF50);  // Green when good health
          }
        }
      }
    });
    
    // Add back button
    this.backButton = this.gameUI.createBackButton(() => {
      console.log('Back button clicked, returning to MainMenuScene');
      
      // Perform thorough cleanup before scene transition
      this.performFinalCleanup();
      
      // Stop this scene first then start the menu
      this.scene.stop('GameScene');
      this.scene.start('MainMenuScene');
    });
    
    // Add fullscreen button using the GameUISystem
    this.fullscreenButton = this.gameUI.createFullscreenButton(20, 60);
    
    // Set up FPS monitor
    this.fpsMonitor = this.gameUI.setupFPSMonitor();
  }

  private setupInput(): void {
    // Add key for canceling movement
    this.input.keyboard?.on('keydown-ESC', () => {
      // Cancel cell movement if ESC is pressed
      if (this.cell) {
        (this.cell as any).targetPosition = null;
      }
      
      // Also exit fullscreen if active
      if (this.scale.isFullscreen) {
        this.scale.stopFullscreen();
      }
    });
    
    // Setup keyboard input
    this.input.keyboard?.on('keydown-SPACE', () => {
      // Toggle phospholipids visibility
      this.cell.togglePhospholipids();
    });
    
    // Reset camera position with R key
    this.input.keyboard?.on('keydown-R', () => {
      this.resetCamera();
    });
  }

  private setupCameraControls(): void {
    // Middle mouse button or touch drag to pan camera
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Check if pointer is over UI elements - with safety check
      const isOverUI = this.uiContainer && this.uiContainer.getBounds() ? 
                       this.uiContainer.getBounds().contains(pointer.x, pointer.y) : 
                       false;
      
      // Only handle middle button or multi-touch for camera panning
      if ((pointer.middleButtonDown() || (pointer as any).touchPoints > 1) && !isOverUI) {
        this.cameraControls.isDragging = true;
        this.cameraControls.lastPointerPosition = { x: pointer.x, y: pointer.y };
        this.cameras.main.stopFollow();
      }
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.cameraControls.isDragging) {
        const deltaX = pointer.x - this.cameraControls.lastPointerPosition.x;
        const deltaY = pointer.y - this.cameraControls.lastPointerPosition.y;
        
        // Update the target pan position
        this.cameraControls.targetPan.x = this.cameras.main.scrollX - deltaX / this.cameras.main.zoom + this.cameras.main.width / 2 / this.cameras.main.zoom;
        this.cameraControls.targetPan.y = this.cameras.main.scrollY - deltaY / this.cameras.main.zoom + this.cameras.main.height / 2 / this.cameras.main.zoom;
        
        // Directly update current pan for immediate response during dragging
        this.cameraControls.currentPan.copy(this.cameraControls.targetPan);
        
        // Apply the pan immediately during drag
        this.cameras.main.centerOn(this.cameraControls.currentPan.x, this.cameraControls.currentPan.y);
        
        this.cameraControls.lastPointerPosition = { x: pointer.x, y: pointer.y };
      }
    });
    
    this.input.on('pointerup', () => {
      this.cameraControls.isDragging = false;
    });
    
    // Mouse wheel to zoom
    this.input.on('wheel', (pointer: any, gameObjects: any, deltaX: number, deltaY: number) => {
      // Ignore very small delta values that might be caused by trackpad or device quirks
      if (Math.abs(deltaY) < 0.5) return;
      
      // Check if pointer is over UI elements - with safety check
      const isOverUI = this.uiContainer && this.uiContainer.getBounds() ? 
                       this.uiContainer.getBounds().contains(pointer.x, pointer.y) : 
                       false;
      if (isOverUI) return; // Don't zoom if over UI
      
      // Store the pointer position for zooming toward that point
      this.cameraControls.zoomPoint.set(pointer.x, pointer.y);
      
      // Use logarithmic scaling for more natural zoom feel
      // This makes zooming more consistent regardless of current zoom level
      const zoomLevel = this.cameraControls.targetZoom;
      
      // Adjust zoom factor based on direction - faster zooming out
      const direction = deltaY > 0 ? 1 : -1;
      const zoomFactor = direction > 0 ? 0.1 : 0.15; // Faster when zooming out
      
      // Apply logarithmic scaling
      const zoomChange = direction * zoomLevel * zoomFactor;
      
      // Apply zoom immediately for better responsiveness
      this.cameraControls.targetZoom = Phaser.Math.Clamp(
        this.cameraControls.targetZoom - zoomChange,
        this.cameraControls.minZoom,
        this.cameraControls.maxZoom
      );
      
      // For zooming out, also update current zoom directly for immediate feedback
      if (direction > 0 && Math.abs(zoomChange) > 0.05) {
        // Move current zoom 30% of the way to target immediately when zooming out
        const currentToTargetDiff = this.cameraControls.targetZoom - this.cameraControls.currentZoom;
        this.cameraControls.currentZoom += currentToTargetDiff * 0.3;
        this.cameras.main.setZoom(this.cameraControls.currentZoom);
      }
    });
    
    // Double tap to center on cell
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if ((pointer as any).downTime - (pointer as any).previousDownTime < 300) {
        this.resetCamera();
      }
    });
    
    // Touch pinch to zoom
    this.input.addPointer(1); // Ensure we can track at least 2 pointers for pinch
    
    let pinchDistance = 0;
    let pinchZoom = 1;
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        const currentDistance = Phaser.Math.Distance.Between(
          this.input.pointer1.x, this.input.pointer1.y,
          this.input.pointer2.x, this.input.pointer2.y
        );
        
        // Ignore very small movements to prevent accidental zooming
        if (pinchDistance > 0 && Math.abs(currentDistance - pinchDistance) > 1) {
          const distanceRatio = currentDistance / pinchDistance;
          
          // Calculate zoom change using logarithmic scaling for consistency with mouse wheel
          // This makes pinch zooming feel more natural and consistent
          const zoomChange = (distanceRatio - 1) * pinchZoom * 0.5;
          
          // Only apply zoom if the change is significant
          if (Math.abs(zoomChange) > 0.005) {
            // Set target zoom for smooth zooming
            this.cameraControls.targetZoom = Phaser.Math.Clamp(
              pinchZoom + zoomChange,
              this.cameraControls.minZoom,
              this.cameraControls.maxZoom
            );
            
            // Get midpoint of the two pointers
            const midX = (this.input.pointer1.x + this.input.pointer2.x) / 2;
            const midY = (this.input.pointer1.y + this.input.pointer2.y) / 2;
            
            // Store the midpoint for zooming toward that point
            this.cameraControls.zoomPoint.set(midX, midY);
            
            // Update pinch zoom for next calculation
            pinchZoom = this.cameraControls.targetZoom;
          }
        }
        
        pinchDistance = currentDistance;
      }
    });
    
    this.input.on('pointerup', () => {
      pinchDistance = 0;
    });
  }
  
  private resetCamera(): void {
    // Center camera on cell and reset zoom
    this.cameraControls.targetZoom = 1;
    
    // Immediately center on cell
    this.cameras.main.centerOn(this.cell.x, this.cell.y);
  }
  
  /**
   * Handle resize events from the scale manager
   */
  private handleResize(gameSize: Phaser.Structs.Size | any): void {
    // Log resize event
    console.log('GameScene handleResize called with size:', gameSize.width, gameSize.height);
    
    // Update UI camera size
    const uiCam = this.cameras.getCamera('uiCam');
    if (uiCam) {
      uiCam.setSize(gameSize.width, gameSize.height);
      console.log('Updated UI camera size to:', gameSize.width, gameSize.height);
    }
    
    // Use the GameUISystem's handleResize method
    if (this.gameUI) {
      this.gameUI.handleResize();
    } else {
      // Fallback to legacy resize handling
      if (this.backButton) {
        this.backButton.setPosition(gameSize.width - 20, 20);
      }
      
      if (this.fullscreenButton) {
        this.fullscreenButton.setPosition(gameSize.width - 20, 60);
      }
      
      // Update any other UI elements that need repositioning
      if (this.uiContainer) {
        this.uiContainer.iterate((child: Phaser.GameObjects.GameObject) => {
          // Reposition any elements that should be aligned to screen edges
          if (child.getData('alignRight')) {
            // Cast to a component type that has setX method
            const component = child as unknown as { setX: (x: number) => void };
            component.setX(gameSize.width - child.getData('rightMargin'));
          }
          if (child.getData('alignBottom')) {
            // Cast to a component type that has setY method
            const component = child as unknown as { setY: (y: number) => void };
            component.setY(gameSize.height - child.getData('bottomMargin'));
          }
          return true;
        });
      }
    }
  }
  
  /**
   * Handle custom window resize event from main.ts
   * This provides more control than the built-in scale resize event
   */
  private handleWindowResized(size: { width: number, height: number }): void {
    console.log('GameScene received windowResized event with size:', size.width, size.height);
    
    // Recenter main camera on the cell if it exists
    if (this.cell) {
      this.cameras.main.centerOn(this.cell.x, this.cell.y);
      console.log('Recentered main camera on cell at:', this.cell.x, this.cell.y);
    }
    
    // Ensure UI elements are positioned correctly
    this.handleResize(size);
  }

  // Helper method for final cleanup
  private performFinalCleanup(): void {
    console.log('GameScene performing final cleanup');
    
    // Clean up gradient background
    if (this.gradientBackground) {
      console.log('Destroying gradient background:', this.gradientBackground.type);
      this.gradientBackground.destroy();
      this.gradientBackground = null as any;
    }
    
    // Clean up any shared resources if we used the texture-based approach
    if (this.textures.exists('gradientTexture')) {
      SharedResources.getInstance().releaseResource('gradientTexture');
    }
    
    // Ensure all graphics objects are destroyed
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Graphics) {
        console.log('Destroying graphics in final cleanup:', child.name);
        child.clear();
        child.destroy();
      }
    });
    
    // Ensure all sprites are destroyed
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        console.log('Destroying sprite in final cleanup:', child.name, child);
        child.destroy();
      }
    });
    
    // Don't remove the gradient texture as it's now shared
    // Remove other textures
    if (this.textures.exists('fullscreen-icon')) {
      console.log('Removing fullscreen icon texture in final cleanup');
      this.textures.remove('fullscreen-icon');
    }
    
    if (this.textures.exists('nutrient')) {
      console.log('Removing nutrient texture in final cleanup');
      this.textures.remove('nutrient');
    }
    
    console.log('GameScene final cleanup complete');
  }

  // Add a shutdown method to clean up when scene stops
  shutdown(): void {
    console.log('GameScene shutdown method called');
    
    // Remove all event listeners
    this.input.keyboard?.off('keydown-ESC');
    this.input.keyboard?.off('keydown-SPACE');
    this.input.keyboard?.off('keydown-R');
    
    this.input.off('pointerdown');
    this.input.off('pointermove');
    this.input.off('pointerup');
    this.input.off('wheel');
    
    // Remove resize listener
    this.scale.off('resize', this.handleResize, this);
    
    // Remove custom resize event listener
    this.events.off('windowResized', this.handleWindowResized, this);
    
    // Perform final cleanup
    this.performFinalCleanup();
    
    // Add FpsMonitor cleanup
    if (this.fpsMonitor) {
      this.fpsMonitor.destroy();
    }
    
    console.log('GameScene shutdown complete');
  }

  init(data: any): void {
    console.log('GameScene init method called');
    
    // Initialize with data from previous scene if available
    if (data) {
      // Carry over data from CytoskeletonScene
      this.membraneStability = data.membraneStability || 100;
      this.cytoskeletonDensity = data.cytoskeletonDensity || 100;
      this.atpEfficiency = data.atpEfficiency || 100;
      
      // Initialize stage-specific properties
      this.hasLysosomes = data.hasLysosomes || false;
      this.hasChloroplasts = data.hasChloroplasts || false;
    }
    
    // Check for and remove any leftover gradient textures
    if (this.textures.exists('gradientTexture')) {
      console.log('Found leftover gradient texture in GameScene init, removing it');
      this.textures.remove('gradientTexture');
    }
    
    // Reset gradient reference
    this.gradientBackground = null as any;
    
    // Check for any leftover sprites from previous instances
    console.log('Checking for leftover sprites in GameScene init');
    let spriteCount = 0;
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        spriteCount++;
        console.log('Found sprite in init:', child.name, child);
      }
    });
    console.log(`Found ${spriteCount} sprites in init`);
  }

  // Debug method to log all Graphics objects in the scene
  private debugLogAllGraphics(): void {
    console.log('--- DEBUG: Logging all Graphics objects in the scene ---');
    let graphicsCount = 0;
    
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Graphics) {
        graphicsCount++;
        console.log(`Graphics #${graphicsCount}:`, 
                    'name:', child.name || 'unnamed', 
                    'position:', child.x, child.y,
                    'depth:', child.depth,
                    'parent:', child.parentContainer ? child.parentContainer.name || 'unnamed container' : 'scene');
      }
    });
    
    console.log(`Total Graphics objects: ${graphicsCount}`);
    console.log('--- END DEBUG LOG ---');
  }

  // Debug method to log all Sprites in the scene
  private debugLogAllSprites(): void {
    console.log('--- DEBUG: Logging all Sprite objects in the scene ---');
    let spriteCount = 0;
    
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        spriteCount++;
        console.log(`Sprite #${spriteCount}:`, 
                    'name:', child.name || 'unnamed', 
                    'position:', child.x, child.y,
                    'depth:', child.depth,
                    'alpha:', child.alpha,
                    'visible:', child.visible,
                    'parent:', child.parentContainer ? child.parentContainer.name || 'unnamed container' : 'scene');
      }
    });
    
    console.log(`Total Sprite objects: ${spriteCount}`);
    console.log('--- END DEBUG LOG ---');
  }

  private createBackground(): void {
    console.log('Creating background for game scene');
    
    // Detect if this is a mobile browser
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Detect if WebGL is supported properly
    const hasWebGL = (this.renderer && this.renderer.type === Phaser.WEBGL);
    
    console.log(`Device info: Mobile=${isMobile}, WebGL=${hasWebGL}, UserAgent=${navigator.userAgent}`);
    
    try {
      // Try to use the shader-based approach first (fastest and best looking)
      const { ShaderGradientBackground } = require('../utils/ShaderGradientBackground');
      
      // Create the shader-based gradient
      this.gradientBackground = ShaderGradientBackground.createForScene(this, 0, 0);
      
      console.log(`Successfully created shader-based gradient background (type: ${this.gradientBackground.type})`);
      
      // Check if we're on Chrome/Android which might need special handling
      if (isMobile && /Chrome/i.test(navigator.userAgent)) {
        console.log('Chrome on Android detected - applying special rendering flags');
        
        // Force immediate render update to ensure shader initializes properly
        if (this.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
          // Only WebGL renderer has this method
          (this.renderer as any).updateTexture(this.gradientBackground.texture);
        }
        
        // If we have a texture, force it to render at full resolution
        if (this.gradientBackground.texture) {
          this.gradientBackground.texture.setFilter(Phaser.Textures.LINEAR);
        }
      }
    } catch (error) {
      console.error('Error creating shader gradient background:', error);
      
      // Fallback to traditional texture-based gradient
      console.log('Falling back to traditional texture-based gradient');
      this.createTraditionalGradient();
    }
  }
  
  private createTraditionalGradient(): void {
    // Detect if this is a mobile browser
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Use the existing texture-based approach as fallback
    const sharedResources = SharedResources.getInstance();
    
    // Create gradient sprite using shared resources
    this.gradientBackground = sharedResources.createGradientSprite(this, 0, 0);
    console.log('Created traditional gradient background using shared resources');
  }

  /**
   * Helper method to ensure a game object is ignored by the UI camera
   * @param gameObject The game object to ignore
   */
  private ensureIgnoredByUICamera(gameObject: Phaser.GameObjects.GameObject): void {
    // Get the UI camera
    const uiCam = this.cameras.getCamera('uiCam');
    if (uiCam) {
      // Make sure UI camera ignores this object
      uiCam.ignore(gameObject);
    }
  }
  
  /**
   * Scans the scene for any movement markers or particle effects that should be ignored by the UI camera
   */
  private ensureAllEffectsIgnoredByUICamera(): void {
    // Get the UI camera
    const uiCam = this.cameras.getCamera('uiCam');
    if (!uiCam) return;
    
    // Scan for objects that need to be ignored
    this.children.list.forEach(child => {
      // Check for movement markers
      if (child.getData && child.getData('type') === 'movementMarker') {
        uiCam.ignore(child);
      }
      
      // Check for particle effects
      if (child instanceof Phaser.GameObjects.Particles.ParticleEmitter || 
          child.type === 'ParticleEmitterManager') {
        uiCam.ignore(child);
      }
    });
  }

  private addNucleusToCell(): void {
    // Check if cell exists
    if (!this.cell) {
      console.error('Cannot add nucleus: Cell does not exist');
      return;
    }
    
    // Create nucleus with a small delay to ensure the scene is fully initialized
    this.time.delayedCall(50, () => {
      try {
        // Create nucleus positioned at the center of the cell (0,0) in cell coordinates
        // This ensures the nucleus appears inside the cell and moves properly with it
        const nucleus = new Nucleus(this, 0, 0, {
          detailLevel: 'high',
          radius: 45,
          chromatinDensity: 0.7,
          wobbleAmount: 0.05, // Very subtle wobble for stability
          color: 0xE6F0F8, // Slightly brighter blue-gray
          alpha: 0.95, // Higher alpha for better visibility through membrane
          outlineColor: 0x3C4F65, // Darker outline for better contrast
          id: 'player_nucleus',
          movementDelay: 0.05 // Extremely low value ensures nucleus stays at cell center
        });
        
        // Add nucleus to the cell container - this makes its coordinates relative to the cell
        this.cell.addOrganelle(nucleus);
        
        console.log('Added nucleus to player cell with high detail level');
      } catch (error) {
        console.error('Error adding nucleus to cell:', error);
      }
    });
  }
} 



